import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton';
import { ArchiveLalSegmentDialogContent } from '@openx/components-segment/lib/components/ArchiveLalSegmentsDialog/ArchiveLalSegmentDialogContent';
import { useLalSegmentsOperations } from '@openx/components-segment/lib/hooks/useLalSegmentsOperations';
import { isLalSegmentDeleted } from '@openx/components-segment/lib/utils/isLalSegmentDeleted';
import { isLalSegmentInProcessing } from '@openx/components-segment/lib/utils/isLalSegmentInProcessing';
import { type LalSegmentApi, lalSegmentRowActionType } from '@openx/types/segments';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

type UseHeaderActionsProps = {
  lalSegment?: LalSegmentApi | null;
};

export const useHeaderActions = ({ lalSegment }: UseHeaderActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { callLalSegmentAction } = useLalSegmentsOperations();

  if (!lalSegment) {
    return { actions: [] };
  }

  const cannotEditOrArchive = isLalSegmentDeleted(lalSegment) || isLalSegmentInProcessing(lalSegment);

  const handleUseLalSegmentAsTemplate = () => {
    navigate('/segments/lookalike/create', {
      state: {
        lalSegment,
        useAsTemplate: true,
      },
    });
  };

  const actions: ActionButtonProps[] = [
    {
      color: 'secondary',
      'data-test': 'edit',
      label: t('Edit'),
      onClick: () => navigate(`/segments/lookalike/${lalSegment.id}/edit`),
      showAsActionButton: true,
      disabled: cannotEditOrArchive,
    },
    {
      color: 'error',
      'data-test': 'archive',
      label: t('Archive'),
      onClick: () => {
        callLalSegmentAction({
          action: lalSegmentRowActionType.DELETE,
          content: <ArchiveLalSegmentDialogContent data={[lalSegment]} />,
          segments: [lalSegment],
          onFinish: () => {
            navigate('/segments/lookalike');
          },
        });
      },
      showAsActionButton: true,
      disabled: cannotEditOrArchive,
    },
    {
      color: 'secondary',
      isAllowed: true,
      'data-test': 'use-as-template',
      label: t('Use As Template'),
      onClick: handleUseLalSegmentAsTemplate,
    },
  ];

  return { actions };
};
